import * as React from 'react';
import {
  CmdInput,
  CmdLabel,
  CmdModal,
  CmdModalFooterButton,
  CmdSimpleSelect,
  CmdSwitch,
} from '@commander-services/gui-components';
import GoogleMapReact from 'google-maps-react-markers';
import { useIntl } from 'react-intl';
import { useForm, Controller } from 'react-hook-form';
import { useRecoilValue } from 'recoil';
import dayjs from 'dayjs';
import Calendar from '../../Forms/ReactHookFormFields/Calendar';
import { IValidationApiResponse } from '../../../Services/FormikValidatorService';
import { IOption } from '../../Forms/CmdField';
import Config from '../../../../config';
import showMessage from '../../Toastr/ToastService';
import * as ManualRideService from './ManualRideService';
import { IVehicles } from '../../Vehicles/types';
import { selectedVehiclesAtom, vehiclesAtom } from '../../../store/recoil/vehicles';
import { getRequest, postRequest, putRequest } from '../../../Services/HttpService';
import { AxiosResponse } from 'axios';

interface ModalFormProps {
  handleClose: () => void;
  updateTable: () => void;
  rideId: string | null;
  vehicleId: string | null;
}

interface Inputs {
  vehicleId: string;
  endTs: Date;
  startTs: Date;
  startAddress: string;
  endAddress: string;
  distanceOrig: number;
  driverId: string;
  isPrivate: boolean;
  startLat?: number;
  startLon?: number;
  endLat?: number;
  endLon?: number;
}
export default function Form(props: ModalFormProps): JSX.Element {
  const { formatMessage: f } = useIntl();
  const vehicles = useRecoilValue<IVehicles>(vehiclesAtom);
  const selectedVehicles = useRecoilValue<number[]>(selectedVehiclesAtom);
  const [validation, setValidation] = React.useState<IValidationApiResponse | null>(null);
  const [driversOptions, setDriversOptions] = React.useState<IOption[]>([]);
  const [vehiclesOptions, setVehiclesOptions] = React.useState<IOption[]>([]);
  const [startTsAllowedRange, setStartTsAllowedRange] = React.useState<Date[] | undefined>(
    undefined
  );
  const [endTsAllowedRange, setEndTsAllowedRange] = React.useState<Date[] | undefined>(undefined);
  const googleMap = React.useRef<google.maps.Map | null>(null);

  const initialValues = {
    vehicleId: '',
    startTs: undefined,
    endTs: undefined,
    startAddress: '',
    endAddress: '',
    distanceOrig: undefined,
    driverId: '',
    isPrivate: false,
  };

  const {
    register,
    handleSubmit,
    trigger,
    watch,
    control,
    setValue,
    setError,
    formState: { errors },
  } = useForm<Inputs>({ mode: 'onTouched', defaultValues: { ...initialValues } });
  // const onSubmit: SubmitHandler<Inputs> = (values) => handleSubmitForm(values);

  const getGeoLocation = (address: string) =>
    new Promise((resolve) => {
      if (googleMap.current) {
        const request = {
          query: address,
          fields: ['all'],
        };

        const service = new window.google.maps.places.PlacesService(googleMap.current);

        service.findPlaceFromQuery(request, (results) => {
          if (results && results[0] && !results[0].business_status) {
            resolve({
              lat: results[0].geometry?.location?.lat(),
              lng: results[0].geometry?.location?.lng(),
              // name: results[0].name,
            });
          } else {
            resolve(null);
          }
        });
      }
    });

  const getIsDatepickersValidated = (endRangeToValue?: Date | string) => {
    const endRangeTo = endRangeToValue || (endTsAllowedRange ? endTsAllowedRange[1] : undefined);
    const dateFrom = dayjs(watch('startTs'));
    const dateTo = dayjs(watch('endTs'));
    if (watch('startTs') && watch('endTs') && !dateTo.isAfter(dateFrom)) {
      setError('startTs', { message: f({ id: 'tripsOverview.modal.newRide.startTs.error' }) });
    } else if (
      watch('startTs') &&
      startTsAllowedRange &&
      startTsAllowedRange[0] &&
      dayjs(startTsAllowedRange[0]).isAfter(dateFrom)
    ) {
      setError('startTs', {
        message: `${f({ id: 'datepicker.minDate' })}: ${dayjs(startTsAllowedRange[0]).format(
          'DD.MM.YYYY HH:mm:ss'
        )}`,
      });
    } else if (
      watch('startTs') &&
      startTsAllowedRange &&
      startTsAllowedRange[1] &&
      dateFrom.isAfter(dayjs(startTsAllowedRange[1]))
    ) {
      setError('startTs', {
        message: `${f({ id: 'datepicker.maxDate' })}: ${dayjs(startTsAllowedRange[1]).format(
          'DD.MM.YYYY HH:mm:ss'
        )}`,
      });
    } else {
      trigger('startTs');
    }
    if (
      watch('endTs') &&
      endTsAllowedRange &&
      endTsAllowedRange[0] &&
      dayjs(endTsAllowedRange[0]).isAfter(dateTo)
    ) {
      setError('endTs', {
        message: `${f({ id: 'datepicker.minDate' })}: ${dayjs(endTsAllowedRange[0]).format(
          'DD.MM.YYYY HH:mm:ss'
        )}`,
      });
    } else if (watch('endTs') && endRangeTo && dateTo.isAfter(dayjs(endRangeTo))) {
      setError('endTs', {
        message: `${f({ id: 'datepicker.maxDate' })}: ${dayjs(endRangeTo).format(
          'DD.MM.YYYY HH:mm:ss'
        )}`,
      });
    } else {
      trigger('endTs');
    }
  };

  const getValidation = async (vehicleId: number): Promise<IValidationApiResponse | null> => {
    if (vehicleId) {
      const response = await ManualRideService.getValidation(vehicleId);
      if (response) {
        setValidation(response);
      }
    }
    return null;
  };

  const handleDefaultDriver = React.useCallback(
    async (vehicleId: string) => {
      const response = await ManualRideService.getDefaultDriver(vehicleId);
      if (response) {
        setValue('driverId', String(response));
      }
    },
    [setValue]
  );

  const getDriverOptions = React.useCallback(
    async (vehicleId: string) => {
      const response: IOption[] | AxiosResponse | false = await getRequest<IOption[]>(`/v2/vehicle/${vehicleId}/ride/form/driverId`);
      if (response) {
        setDriversOptions(response as IOption[]);
        if (!props.rideId) {
          handleDefaultDriver(vehicleId);
        }
      }
    },
    [handleDefaultDriver, props.rideId]
  );

  React.useEffect(() => {
    const selectedVehiclesIds = selectedVehicles.map((i) => String(i));
    const filteredVehicles = Object.values(vehicles).filter((i) =>
      selectedVehiclesIds.includes(String(i.id))
    );
    if (!props.rideId && filteredVehicles.length === 1) {
      const selectedVehicleId = String(filteredVehicles[0].id);
      setValue('vehicleId', selectedVehicleId);
      getValidation(Number(selectedVehicleId));
      getDriverOptions(selectedVehicleId);
      handleDefaultDriver(selectedVehicleId);
    }
    setVehiclesOptions(
      filteredVehicles.map((i) => {
        return {
          item: `${i.licenceNumber} - ${i.name}`,
          value: String(i.id),
        };
      })
    );
  }, [selectedVehicles, vehicles, props.rideId, setValue, getDriverOptions, handleDefaultDriver]);

  React.useEffect(() => {
    // const options = getVehiclesOptions();
    // if (vehiclesOptions && vehiclesOptions.length > 0 && (!options || options.length === 0)) {
    if (selectedVehicles.length === 0) {
      showMessage(
        f({ id: 'toastr.warning' }),
        f({ id: 'tripsOverview.modal.newRide.vehicleId.error' }),
        'warning'
      );
    }
  }, [f, selectedVehicles.length]);

  const validateLatLng = (value: string) => {
    const regex =
      /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/;

    const match = value.match(regex);
    if (match) {
      return match;
    }
    return false;
  };

  const onSubmit = async (formValues: Inputs) => {
    const values = { ...formValues };
    let geoLocationValidated = true;

    const startLatLngArray = validateLatLng(values.startAddress);
    if (startLatLngArray && startLatLngArray[1] && startLatLngArray[4]) {
      values.startLat = Number(startLatLngArray[1]);
      values.startLon = Number(startLatLngArray[4]);
    } else {
      const startLatLng: any = await getGeoLocation(values.startAddress);
      if (!startLatLng) {
        setError('startAddress', {
          message: f({ id: 'tripsOverview.modal.newRide.address.error' }),
        });
        geoLocationValidated = false;
      } else {
        values.startLat = startLatLng.lat;
        values.startLon = startLatLng.lng;
        // values.startAddress = startLatLng.name;
        // setValue('startAddress', startLatLng.name);
      }
    }

    const endLatLngArray = validateLatLng(values.endAddress);
    if (endLatLngArray && endLatLngArray[1] && endLatLngArray[4]) {
      values.endLat = Number(endLatLngArray[1]);
      values.endLon = Number(endLatLngArray[4]);
    } else {
      const endLatLng: any = await getGeoLocation(values.endAddress);
      if (!endLatLng) {
        setError('endAddress', {
          message: f({ id: 'tripsOverview.modal.newRide.address.error' }),
        });
        geoLocationValidated = false;
      } else {
        values.endLat = endLatLng.lat;
        values.endLon = endLatLng.lng;
        // values.endAddress = endLatLng.name;
        // setValue('endAddress', endLatLng.name);
      }
    }

    if (geoLocationValidated) {
      const response: AxiosResponse | false =
        props.rideId && props.vehicleId
          ? await putRequest(`/v2/vehicle/${props.vehicleId}/ride/${props.rideId}`, values, { returnAxiosResponse: true})
          : await postRequest(`/v2/vehicle/${values.vehicleId}/ride`, values, { returnAxiosResponse: true });
      if (response && response.data) {
        if (response.status === 200 || response.status === 201 || response.status === 202) {
          props.handleClose();
          props.updateTable();
        } else if (response.status === 400 && response.data.errors) {
          Object.keys(response.data.errors).forEach((key: string) => {
            if (key !== 'endLat' && key !== 'endLon' && key !== 'startLat' && key !== 'startLon') {
              setError(key, { message: response.data.errors[key][0] });
            }
          });
        }
      }
    }
  };

  const getAddress = async () => {
    if (watch('vehicleId') && watch('startTs')) {
      const startTs = new Date(watch('startTs')).toISOString();

      const response = await ManualRideService.getAddress(Number(watch('vehicleId')), startTs);
      if (response) {
        if (response.prevRide) {
          setValue('startAddress', response.prevRide.address);
        }
        if (response.nextRide) {
          setValue('endAddress', response.nextRide.address);
          setEndTsAllowedRange([
            endTsAllowedRange ? endTsAllowedRange[0] : undefined,
            response.nextRide.timestamp,
          ]);
        } else {
          setEndTsAllowedRange([endTsAllowedRange ? endTsAllowedRange[0] : undefined, new Date()]);
        }
        getIsDatepickersValidated(response.nextRide ? response.nextRide.timestamp : new Date());
        trigger('startAddress');
        trigger('endAddress');
      }
    }
    return null;
  };

  const getData = React.useCallback(async () => {
    const response: AxiosResponse | false = await getRequest(`/v2/vehicle/${props.vehicleId}/ride/${props.rideId}`, { returnAxiosResponse: true });
    if (response && response.data && response.status === 200) {
      Object.keys(response.data).forEach((key: string) => {
        setValue(key, response.data[key]);
      });
    }
  }, [props.rideId, props.vehicleId, setValue]);

  React.useEffect(() => {
    if (validation) {
      if (validation.startTs) {
        const minDate = validation.startTs.find((i) => i.param === 'minDate');
        const maxDate = validation.startTs.find((i) => i.param === 'maxDate');
        if (minDate || maxDate) {
          setStartTsAllowedRange([
            startTsAllowedRange && startTsAllowedRange[0] ? startTsAllowedRange[0] : minDate?.value,
            // startTsAllowedRange && startTsAllowedRange[1] ? startTsAllowedRange[1] : maxDate?.value,
            startTsAllowedRange && startTsAllowedRange[1]
              ? startTsAllowedRange[1]
              : dayjs().toISOString(),
          ]);
        }
      }
      if (validation.endTs) {
        const minDate = validation.endTs.find((i) => i.param === 'minDate');
        const maxDate = validation.endTs.find((i) => i.param === 'maxDate');
        if (minDate || maxDate) {
          setEndTsAllowedRange([
            endTsAllowedRange && endTsAllowedRange[0] ? endTsAllowedRange[0] : minDate?.value,
            endTsAllowedRange && endTsAllowedRange[1] ? endTsAllowedRange[1] : maxDate?.value,
          ]);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validation]);

  const buttons: CmdModalFooterButton[] = [
    {
      id: 'modalClose',
      type: 'button',
      title: f({ id: 'form.close' }),
      className: 'e-button',
      closeCallback: props.handleClose,
      tooltip: f({ id: 'alarms.modal.recipient.close.tooltip' }),
    },
    {
      id: 'submitAddForm',
      type: 'submit',
      title: f({ id: 'form.save' }),
      className: 'e-button e-button--gray',
      submitCallback: handleSubmit(onSubmit),
      tooltip: f({ id: 'form.save' }),
    },
  ];

  React.useEffect(() => {
    if (props.rideId && props.vehicleId) {
      getValidation(Number(props.vehicleId));
      getDriverOptions(props.vehicleId);
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.rideId]);

  const getdDistanceOrigvalidation = (param: string) => {
    if (validation && validation.distanceOrig) {
      const validationData = validation.distanceOrig.find((i) => i.param === param);
      if (validationData) {
        return { value: validationData.value, message: validationData.message };
      }
      return {};
    }
    return {};
  };

  const getVehicleName = () => {
    const vehicleData = vehiclesOptions.find((i) => String(i.value) === String(watch('vehicleId')));
    if (vehicleData) {
      return vehicleData.item;
    }
    return '';
  };

  const handleSwicher = (value: string) => {
    setValue('isPrivate', String(value) === '1');
    trigger('isPrivate');
  };

  return (
    <CmdModal
      id="new-ride-react-hook-form"
      title={
        props.rideId
          ? f({ id: `tripsOverview.modal.newRide.title.edit` })
          : f({ id: `tripsOverview.modal.newRide.title.add` })
      }
      footerButtons={buttons}
      // loader={isLoaderActive}
    >
      <form
      // onSubmit={handleSubmit(onSubmit)}
      >
        {props.rideId ? (
          <CmdLabel title={f({ id: 'tripsOverview.modal.vehicle' })} value={getVehicleName()} />
        ) : (
          <CmdSimpleSelect
            id="vehicleId"
            label={f({ id: 'tripsOverview.modal.vehicle' })}
            value={watch('vehicleId')}
            {...register('vehicleId', { required: f({ id: 'form.required' }) })}
            introOption={f({ id: 'form.choose' })}
            options={vehiclesOptions}
            required={true}
            onChangeCallback={(value) => {
              setValue('vehicleId', String(value));
              trigger('vehicleId');
              if (value) {
                getValidation(value);
                getDriverOptions(value);
                getAddress();
              }
            }}
            error={errors.vehicleId?.message}
            touched={true}
            isWithSearchInput={true}
            searchPlaceholder={f({ id: 'form.search' })}
          />
        )}
        {watch('vehicleId') && validation ? (
          !props.rideId ? (
            <Calendar
              id="startTs"
              value={watch('startTs')}
              {...register('startTs', { required: f({ id: 'form.required' }) })}
              mode="single"
              label={f({ id: 'tripsOverview.modal.newRide.startTs' })}
              // tooltip={f({ id: 'alarms.section.basicInformation.validFrom.tooltip' })}
              dateFormat="dd.MM.yyyy HH:mm"
              required={true}
              onChangeCallback={(value: Date) => {
                value.setSeconds(0);
                value.setMilliseconds(0);
                const convertedValue = new Date(value);
                setValue('startTs', convertedValue);
                // getIsDatepickersValidated();
                getAddress();
                trigger('startTs');
              }}
              error={errors.startTs?.message}
              touched={true}
              allowedRange={startTsAllowedRange}
              disabled={!!props.rideId}
            />
          ) : (
            <CmdLabel
              title={f({ id: 'tripsOverview.modal.newRide.startTs' })}
              value={dayjs(watch('startTs')).format('DD.MM.YYYY HH:mm:ss')}
            />
          )
        ) : (
          <CmdLabel
            title={f({ id: 'tripsOverview.modal.newRide.startTs' })}
            value={f({ id: 'form.placeholders.corrections.selectVehicleFirst' })}
          />
        )}
        <CmdInput
          id="startAddress"
          label={f({ id: 'tripsOverview.modal.newRide.startAddress' })}
          {...register('startAddress', { required: f({ id: 'form.required' }) })}
          placeholder={
            watch('vehicleId') ? '' : f({ id: 'form.placeholders.corrections.selectVehicleFirst' })
          }
          required={true}
          onChangeCallback={(value) => {
            setValue('startAddress', value);
            trigger('startAddress');
          }}
          error={errors.startAddress?.message}
          touched={true}
          type="text"
          value={watch('startAddress')}
          disabled={!watch('vehicleId')}
        />

        {watch('vehicleId') && watch('startTs') && validation ? (
          !props.rideId ? (
            <Calendar
              id="endTs"
              value={watch('endTs')}
              {...register('endTs', { required: f({ id: 'form.required' }) })}
              mode="single"
              label={f({ id: 'tripsOverview.modal.newRide.endTs' })}
              // tooltip={f({ id: 'alarms.section.basicInformation.validTo.tooltip' })}
              dateFormat="dd.MM.yyyy HH:mm"
              required={true}
              onChangeCallback={(value: Date) => {
                value.setSeconds(0);
                value.setMilliseconds(0);
                const convertedValue = new Date(value);
                setValue('endTs', convertedValue);
                getIsDatepickersValidated();
                // getAddress();
                // trigger('endTs');
              }}
              error={errors.endTs?.message}
              touched={true}
              allowedRange={endTsAllowedRange}
              // disabled={!!props.rideId}
            />
          ) : (
            <CmdLabel
              title={f({ id: 'tripsOverview.modal.newRide.endTs' })}
              value={dayjs(watch('endTs')).format('DD.MM.YYYY HH:mm:ss')}
            />
          )
        ) : (
          <CmdLabel
            title={f({ id: 'tripsOverview.modal.newRide.endTs' })}
            value={f({ id: 'tripsOverview.modal.newRide.endTs.placeholder' })}
          />
        )}
        <CmdInput
          id="endAddress"
          label={f({ id: 'tripsOverview.modal.newRide.endAddress' })}
          {...register('endAddress', { required: f({ id: 'form.required' }) })}
          placeholder={
            watch('vehicleId') ? '' : f({ id: 'form.placeholders.corrections.selectVehicleFirst' })
          }
          required={true}
          onChangeCallback={(value) => {
            setValue('endAddress', value);
            trigger('endAddress');
          }}
          error={errors.endAddress?.message}
          touched={true}
          type="text"
          value={watch('endAddress')}
          disabled={!watch('vehicleId')}
        />

        <Controller
          name="distanceOrig"
          control={control}
          rules={{
            required: { value: true, message: f({ id: 'form.required' }) },
            min: getdDistanceOrigvalidation('min'),
            max: getdDistanceOrigvalidation('max'),
          }}
          render={({ field }) => (
            <CmdInput
              {...field}
              id="distanceOrig"
              label={f({ id: 'reports.rideDistance' })}
              // placeholder={f({ id: 'form.reports.rideDistance;' })}
              onChangeCallback={(value: number) => {
                setValue('distanceOrig', value);
                trigger('distanceOrig');
              }}
              error={errors.distanceOrig?.message}
              touched={true}
              type="number"
              required={true}
              value={watch('distanceOrig')}
              currency="km"
            />
          )}
        />
        <CmdSimpleSelect
          id="driverId"
          label={f({ id: 'tripsOverview.modal.driver' })}
          value={watch('driverId')}
          {...register('driverId')}
          introOption={
            watch('vehicleId')
              ? f({ id: 'form.choose' })
              : f({ id: 'form.placeholders.corrections.selectVehicleFirst' })
          }
          onChangeCallback={(value) => {
            setValue('driverId', String(value));
            trigger('driverId');
          }}
          options={driversOptions}
          disabled={!watch('vehicleId')}
          isWithSearchInput={true}
          searchPlaceholder={f({ id: 'form.search' })}
        />
        <CmdSwitch
          id="isPrivate"
          label={f({ id: 'form.inputs.vehicles.defaultPrivate' })}
          {...register('isPrivate')}
          touched={true}
          value={watch('isPrivate') ? '1' : '0'}
          setValue={handleSwicher}
        />
      </form>
      <GoogleMapReact
        defaultZoom={8}
        defaultCenter={{ lat: 48.75946, lng: 17.29349 }}
        apiKey={Config.GoogleMaps.apiKey}
        libraries={Config.GoogleMaps.libraries}
        loadScriptExternally={true}
        status="ready"
        onGoogleApiLoaded={({ map }) => {
          googleMap.current = map;
        }}
      />
    </CmdModal>
  );
}
